<script setup lang="ts">
/**
 * LumiRunCard — one action card (issue 534). Private to LumiAssistantPanel.vue
 * (not exported from index.ts): the panel places one of these under the
 * assistant message of the turn that produced it, or — while the turn is
 * still streaming — in the trailing group at the end of the thread. Shares
 * the `.lumi-assistant__*` class family with the panel's own stylesheet on
 * purpose: it is only ever mounted from inside it (see LumiMarkdown.vue's
 * header for why a PUBLIC primitive does not get to make that assumption).
 *
 * Collapsed, the card never claims more than the run's own status allows: the
 * title is the module's own words when it has them (`draftJson.summary`),
 * then the failure prose (`errorMessage`), then a humanised tool name — never
 * "done" for anything but `succeeded`. Expanded reveals the summary in full,
 * the failure reason, and — when the run named what it touched — one "Öffnen"
 * link. A card with nothing to reveal beyond its own title has no toggle at
 * all and shows the title in full instead (issue 557): an expander that only
 * repeats the heading is noise. `needs_approval`'s draft view and Approve/Cancel stay unconditional on
 * the toggle, exactly as before this card existed: a person should not have to
 * expand a card to act on it.
 */
import { computed, ref } from 'vue';
import type { AssistantLabels } from './LumiAssistantPanel.vue';
import type { AssistantApproveEntry, AssistantDraftEdit, AssistantDraftEntry, AssistantRunView } from './useLumiAssistant';
import LumiIcon from '../icons/LumiIcon.vue';

const TERMINAL = new Set(['succeeded', 'failed', 'cancelled']);

const props = defineProps<{
  run: AssistantRunView;
  labels: AssistantLabels;
  draft?: AssistantDraftEntry;
  edit?: AssistantDraftEdit;
  approveState?: AssistantApproveEntry;
  /** Resolve the run's `entityType`/`entityId` to an in-app path — same shape
   *  and same resolver as the transport's entity-link handling. */
  resolveLink?: (ref: { module: string; type: string; id: string; label: string }) => { path: string; moduleKey?: string } | null;
  navigate?: (path: string, moduleKey?: string) => void;
}>();

const emit = defineEmits<{ 'edit-draft-body': [value: string]; approve: []; cancel: [] }>();

const expanded = ref(false);

// See the class's own doc comment above: an errorMessage/summary-less run
// still needs SOME readable words, so the fallback humanises the tool name
// rather than showing a raw identifier.
function humanizeToolName(name: string): string {
  const spaced = name.replace(/[_-]+/g, ' ').trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : name;
}

const summary = computed<string | undefined>(() => {
  const s = props.run.draftJson?.summary;
  return typeof s === 'string' && s.trim() ? s : undefined;
});

// A summary may run to several lines (ADR 0106 D7: a paper timesheet's card
// names every day it would write). The first line is the title; the rest is
// what the person approves, so it shows with the approve buttons, not behind
// the details toggle.
const summaryLines = computed<string[]>(() => (summary.value ? summary.value.split('\n').slice(1).filter((l) => l.trim()) : []));

const title = computed(() => {
  if (summary.value) return summary.value.split('\n')[0];
  if (props.run.errorMessage && props.run.errorMessage.trim()) return props.run.errorMessage;
  return humanizeToolName(props.run.toolName || props.run.moduleKey || props.run.id);
});

// The pill (issue 534). `gateOutcome` overrides the plain status word ONLY on
// a settled run and only for refused/expired — everywhere else the status
// speaks for itself. Never returns the "succeeded" word for anything but an
// actually-succeeded run (the sad-path invariant this card exists to hold).
const statusLabel = computed(() => {
  const L = props.labels;
  const r = props.run;
  if (TERMINAL.has(r.status) && r.gateOutcome === 'refused') return L.runStatusRefused;
  if (TERMINAL.has(r.status) && r.gateOutcome === 'expired') return L.runStatusExpired;
  switch (r.status) {
    case 'needs_approval':
      return L.needsApproval;
    case 'succeeded':
      return L.runStatusSucceeded;
    case 'failed':
      return L.runStatusFailed;
    case 'cancelled':
      return L.runStatusCancelled;
    case 'queued':
    case 'running':
      return L.runStatusRunning;
    default:
      return r.status;
  }
});

// The "Öffnen" link (issue 534): reuses the SAME resolver the entity-link
// tokens in the answer text use — a run naming a type with no route shows no
// link at all, exactly like an unresolved `[[…]]` renders as plain text.
//
// A booking is the one exception (F6, review pass): what it touched is the
// entry ROW (`entityType: 'time_entry'`), but time-ui has no entry-detail
// route to send anyone to — only the day it landed on, exactly what the
// settling message's own `[[time/day:<date>|…]]` link already points at. The
// plan the propose call recorded carries that date, so the card resolves the
// same day link rather than a route that does not exist.
const openTarget = computed(() => {
  const { entityType, entityId, moduleKey } = props.run;
  if (entityType === 'time_entry') {
    const date = props.run.draftJson?.date;
    if (typeof date !== 'string' || !date) return null;
    return props.resolveLink?.({ module: 'time', type: 'day', id: date, label: props.labels.runOpen }) ?? null;
  }
  if (!entityType || !entityId) return null;
  return props.resolveLink?.({ module: moduleKey || '', type: entityType, id: entityId, label: props.labels.runOpen }) ?? null;
});
// Whether expanding would show anything the title does not already say. The
// summary IS the title whenever there is one, so only a failure reason that
// differs from it, or an "Öffnen" link, counts (issue 557).
const hasDetail = computed(() => {
  const r = props.run;
  const distinctError = r.status === 'failed' && !!r.errorMessage && r.errorMessage !== title.value;
  return distinctError || !!openTarget.value;
});

// A synchronous `kind:'draft'` leaves no module-side row to read, so
// assistant-api answers `DRAFT_NOT_AVAILABLE` (agent.service readRunDraft):
// the run's summary — the card's own title — is all there is to approve. That
// is an expected, settled state, not a failure: the card shows no draft block
// and Approve works (issue 557 — it used to stay disabled for good, because
// Approve waited for a 'loaded' draft that could never arrive). Every other
// refusal (purged, not permitted, read failed) keeps Approve disabled: there
// WAS something to show and the approver has not seen it.
const noDraftToShow = computed(() => props.draft?.status === 'error' && props.draft.reason === 'DRAFT_NOT_AVAILABLE');
const draftSettled = computed(() => props.draft?.status === 'loaded' || noDraftToShow.value);

function onOpen() {
  if (openTarget.value) props.navigate?.(openTarget.value.path, openTarget.value.moduleKey);
}

// A draft read refusal is a CODE (assistant-tools.v1 §2.5) so the card can say
// which of them happened rather than "something went wrong".
function draftErrorLabel(reason: string | undefined): string {
  const L = props.labels;
  switch (reason) {
    case 'DRAFT_NOT_AVAILABLE':
      return L.draftErrorNotAvailable;
    case 'DRAFT_NOT_FOUND':
      return L.draftErrorNotFound;
    case 'DRAFT_NO_MESSAGE':
      return L.draftErrorNoMessage;
    case 'permission':
      return L.draftErrorPermission;
    default:
      return L.draftErrorGeneric;
  }
}
function approveErrorLabel(reason: string | undefined): string {
  return reason === 'permission' ? props.labels.approveErrorPermission : props.labels.approveErrorGeneric;
}

function onDraftBodyInput(event: Event) {
  emit('edit-draft-body', (event.target as HTMLTextAreaElement).value);
}
</script>

<template>
  <div class="lumi-assistant__run">
    <!-- Pill on its own row, top right, the title the full width under it: the
         panel is too narrow for the two side by side (#1064). -->
    <div class="lumi-assistant__run-head">
      <span
        class="lumi-assistant__run-status"
        :class="{ 'lumi-assistant__run-status--approval': run.status === 'needs_approval' }"
      >{{ statusLabel }}</span>
      <button
        v-if="hasDetail"
        type="button"
        class="lumi-assistant__run-toggle"
        :class="{ 'lumi-assistant__run-toggle--open': expanded }"
        :aria-expanded="expanded"
        :aria-label="expanded ? labels.runDetailsHide : labels.runDetailsShow"
        :title="expanded ? labels.runDetailsHide : labels.runDetailsShow"
        @click="expanded = !expanded"
      >
        <LumiIcon name="expand" width="14" height="14" stroke-width="2" />
      </button>
    </div>
    <span class="lumi-assistant__run-name" :class="{ 'lumi-assistant__run-name--full': !hasDetail }">{{ title }}</span>

    <div v-if="expanded && hasDetail" class="lumi-assistant__run-detail">
      <p v-if="summary" class="lumi-assistant__run-summary">{{ summary }}</p>
      <p v-if="run.status === 'failed' && run.errorMessage" class="lumi-assistant__run-error" role="alert">{{ run.errorMessage }}</p>
      <button v-if="openTarget" type="button" class="lumi-assistant__run-open" @click="onOpen">{{ labels.runOpen }}</button>
    </div>

    <!-- Unconditional on `expanded` — stays exactly as it worked before this
         card existed: a person acts on this without having to open anything. -->
    <ul v-if="summaryLines.length" class="lumi-assistant__run-lines" data-testid="lumi-run-lines">
      <li v-for="(line, i) in summaryLines" :key="i">{{ line }}</li>
    </ul>

    <div v-if="run.status === 'needs_approval'" class="lumi-assistant__run-actions">
      <!-- The card fetches the draft when it opens (#275, D3): an empty card
           must never look like an approvable one, so Approve below stays
           disabled until this reaches 'loaded' — or until the answer is that
           there is no draft beyond the summary at all, which shows nothing. -->
      <div v-if="(draft?.status ?? 'loading') === 'loading'" class="lumi-assistant__draft-state">{{ labels.draftLoading }}</div>
      <div v-else-if="draft?.status === 'error' && !noDraftToShow" class="lumi-assistant__draft-state lumi-assistant__draft-state--error" role="alert">
        {{ draftErrorLabel(draft?.reason) }}
      </div>
      <div v-else-if="draft?.status === 'loaded'" class="lumi-assistant__draft-fields">
        <div v-if="draft?.draft?.candidateName" class="lumi-assistant__draft-field">
          <span class="lumi-assistant__draft-field-label">{{ labels.draftCandidate }}</span>
          <span>{{ draft?.draft?.candidateName }}</span>
        </div>
        <div v-if="draft?.draft?.applicationNumber" class="lumi-assistant__draft-field">
          <span class="lumi-assistant__draft-field-label">{{ labels.draftApplication }}</span>
          <span>{{ draft?.draft?.applicationNumber }}</span>
        </div>
        <div v-if="draft?.draft?.jobTitle" class="lumi-assistant__draft-field">
          <span class="lumi-assistant__draft-field-label">{{ labels.draftJob }}</span>
          <span>{{ draft?.draft?.jobTitle }}</span>
        </div>
        <div v-if="draft?.draft?.summary" class="lumi-assistant__draft-field">
          <span class="lumi-assistant__draft-field-label">{{ labels.draftSummary }}</span>
          <span>{{ draft?.draft?.summary }}</span>
        </div>
        <div v-if="draft?.draft?.subject" class="lumi-assistant__draft-field">
          <span class="lumi-assistant__draft-field-label">{{ labels.draftSubject }}</span>
          <span>{{ draft?.draft?.subject }}</span>
        </div>
        <div v-if="draft?.draft?.bodyText !== undefined" class="lumi-assistant__draft-field">
          <span :id="`lumi-assistant-draft-body-label-${run.id}`" class="lumi-assistant__draft-field-label">
            {{ labels.draftBody }}
            <span v-if="edit?.dirty" class="lumi-assistant__draft-dirty">({{ labels.draftEdited }})</span>
          </span>
          <!-- TEXT only (#275, D11): a reject draft's HTML body is never
               editable here, and never sent — see useLumiAssistant's
               approveRun, which sends only bodyText. -->
          <textarea
            class="lumi-assistant__draft-textarea"
            rows="4"
            :aria-labelledby="`lumi-assistant-draft-body-label-${run.id}`"
            :value="edit?.bodyText"
            @input="onDraftBodyInput"
          />
        </div>
      </div>

      <div v-if="approveState?.error" class="lumi-assistant__draft-state lumi-assistant__draft-state--error" role="alert">
        {{ approveErrorLabel(approveState?.error?.reason) }}
      </div>

      <button
        type="button"
        class="lumi-assistant__btn"
        :disabled="approveState?.saving || !draftSettled"
        @click="emit('approve')"
      >{{ labels.approve }}</button>
      <button type="button" class="lumi-assistant__btn lumi-assistant__btn--ghost" @click="emit('cancel')">{{ labels.cancel }}</button>
    </div>
  </div>
</template>
